/*
 * Purpose: Human approval UI for risky Mission items — AI may propose; human approves use
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { AlertTriangle, CheckCircle2, Lightbulb } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { OutcomePlan } from '@/services/autoblocks/types';
import {
  extractRiskItemsFromPlan,
  getPendingRiskApprovals,
  MISSION_CREATIVE_FREEDOM_NOTICE,
  MISSION_RISK_LABELS,
  requiresHumanApproval,
} from '@/lib/missionRiskPolicy';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { cn } from '@/lib/utils';

interface MissionRiskReviewPanelProps {
  plan: OutcomePlan;
  className?: string;
}

export function MissionRiskReviewPanel({ plan, className }: MissionRiskReviewPanelProps) {
  const riskApprovals = useMissionKnowledgeStore((s) => s.riskApprovals);
  const approveRiskItem = useMissionKnowledgeStore((s) => s.approveRiskItem);

  const items = extractRiskItemsFromPlan(plan);
  const pending = getPendingRiskApprovals(items, riskApprovals);
  const riskyItems = items.filter((i) => requiresHumanApproval(i.level));

  if (riskyItems.length === 0) return null;

  return (
    <div
      className={cn(
        'rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-3',
        className
      )}
      id="mission-risk-review"
    >
      <div className="flex items-start gap-2">
        <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground">AI proposals &amp; risk review</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            {MISSION_CREATIVE_FREEDOM_NOTICE}
          </p>
        </div>
      </div>

      <ul className="space-y-2">
        {riskyItems.map((item) => {
          const approved = Boolean(riskApprovals[item.id]?.approvedAt);
          const meta = MISSION_RISK_LABELS[item.level];
          return (
            <li
              key={item.id}
              className={cn(
                'rounded-md border px-3 py-2 text-xs space-y-2',
                approved ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border/60 bg-background/80'
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                {approved ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" aria-hidden />
                )}
                <span className="font-medium text-foreground">{item.summary}</span>
                <Badge
                  variant={item.level === 'critical' ? 'destructive' : 'outline'}
                  className="text-[10px]"
                >
                  {meta.label}
                </Badge>
              </div>
              {item.detail && (
                <p className="text-[11px] text-muted-foreground leading-relaxed">{item.detail}</p>
              )}
              {!approved && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-7 text-[11px]"
                  onClick={() => approveRiskItem(item.id, item.summary)}
                >
                  Approve for this Mission
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {pending.length > 0 && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400">
          {pending.length} risky item{pending.length === 1 ? '' : 's'} still need approval before live
          run.
        </p>
      )}
    </div>
  );
}
